/**
 * ============================================================================
 * CalibrationTaskCard — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: render a single calibration task as a card. Shows equipment tag,
 *          name, criticality chip, days-until/since-due pill, last cal date,
 *          and the technician hint. "Record Calibration" CTA opens the
 *          RecordCalibrationDialog.
 *
 * Mobile-friendly: the card stacks under 720 px so it remains readable on a
 *  phone where field engineers actually live.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './CalibrationTray.module.css';
import type { CalibrationTask } from './useCalibrationTrayQuery';
import type { EquipmentCriticality } from '../EquipmentWorkbench/useEquipmentQuery';

const CRIT_CLASS: Record<EquipmentCriticality, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  NON_GMP: styles.critNonGmp,
};

export interface CalibrationTaskCardProps {
  task: CalibrationTask;
  onRecord: (task: CalibrationTask) => void;
}

export function CalibrationTaskCard({
  task,
  onRecord,
}: CalibrationTaskCardProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const d = task.days_until_due;
  const completed = task.bucket === 'RECENTLY_COMPLETED';
  let pillClass: string;
  let pillLabel: string;
  if (completed) {
    pillClass = styles.dueDone;
    pillLabel = t('equipment.calibrationTray.completed', {
      defaultValue: 'Completed',
    });
  } else if (d < 0) {
    pillClass = styles.dueOverdue;
    pillLabel = t('equipment.calibrationTray.overdueDays', {
      defaultValue: '{{n}} days overdue',
      n: nf.format(Math.abs(d)),
    });
  } else if (d <= 7) {
    pillClass = styles.dueSoon;
    pillLabel = t('equipment.calibrationTray.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  } else {
    pillClass = styles.dueLater;
    pillLabel = t('equipment.calibrationTray.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  }

  return (
    <article
      className={styles.card}
      aria-label={t('equipment.calibrationTray.cardAria', {
        defaultValue: 'Calibration task for {{tag}} ({{name}})',
        tag: task.equipment_tag,
        name: task.equipment_name,
      })}
    >
      <div className={styles.cardMain}>
        <div className={styles.cardTitleRow}>
          <code className={styles.tagMono}>{task.equipment_tag}</code>
          <h3 className={styles.cardName}>{task.equipment_name}</h3>
          <span className={`${styles.chip} ${CRIT_CLASS[task.criticality]}`}>
            {t(`equipment.criticality.${task.criticality}`, {
              defaultValue: task.criticality,
            })}
          </span>
          <span className={`${styles.duePill} ${pillClass}`}>{pillLabel}</span>
        </div>
        <div className={styles.cardMeta}>
          <span>
            {t('equipment.calibrationTray.due', {
              defaultValue: 'Due: {{ts}}',
              ts: dateFmt.format(new Date(task.due_at_utc)),
            })}
          </span>
          <span>
            {task.last_calibration_at_utc
              ? t('equipment.calibrationTray.lastCal', {
                  defaultValue: 'Last cal: {{ts}}',
                  ts: dateFmt.format(new Date(task.last_calibration_at_utc)),
                })
              : t('equipment.calibrationTray.lastCalNone', {
                  defaultValue: 'Last cal: never',
                })}
          </span>
          {task.technician_hint && (
            <span>
              {t('equipment.calibrationTray.technician', {
                defaultValue: 'Technician: {{name}}',
                name: task.technician_hint,
              })}
            </span>
          )}
        </div>
      </div>
      <div className={styles.cardActions}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onRecord(task)}
          disabled={completed}
          aria-label={t('equipment.calibrationTray.recordAria', {
            defaultValue: 'Record calibration for {{tag}}',
            tag: task.equipment_tag,
          })}
        >
          {t('equipment.calibrationTray.record', {
            defaultValue: 'Record calibration',
          })}
        </Button>
      </div>
    </article>
  );
}
